import {
    useGetBrownlowPredictorQuery,
    useGetMatchesQuery,
    useGetRoundsQuery
} from 'common/store/modules/api/afl-api';
import { useEffect, useMemo, useState } from 'react';
import { useGetBFAwardsLeaderboardQuery } from 'common/store/modules/api/cfs-api';
import { useLeaderboardRefetch } from './use-leaderboard-refetch';
import { useCurrentPlayerPulseIds } from './use-current-player-pulse-ids';
import {
    composeColumnData,
    composeRowData,
    getLeaderboardWithAllMatches
} from '../utils';

const MAX_ROUNDS = 50;
const DEFAULT_PAGE_SIZE = 20;

/**
 * Returns brownlow leaderboard data combined with match data and predicted votes
 *
 * @param {object} props
 * @param {string} props.competitionId Competition id
 * @param {string} props.compSeasonPid Competition provider id
 * @param {string} props.compSeasonId Competition id
 * @param {number} props.currentPage Current page displayed. Starting from 1
 * @param {number} props.pageSize Number of items displayed per page
 * @param {string} props.clubFilter Club abbreviation to filter the leadearboard for. Default "all"
 * @param {boolean} props.disablePolling If TRUE, the polling won't be initiated event if the leadeboard is in LIVE state. Default FALSE
 *
 * @returns {object} { data: { leaderboard:Array, status:string }, rounds:Array, isLoading:boolean }
 */
export const useLeaderboardDataWithPredictedVotes = ({
    competitionId,
    compSeasonPid = '',
    compSeasonId,
    currentPage = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    clubFilter = 'all',
    disablePolling = false
}) => {
    const [currentPlayerIds, setCurrentPlayerIds] = useState([]);
    const [data, setData] = useState([]);

    /**
     * Fetching Rounds for the compseason
     */
    const {
        data: rounds,
        isFetching: isRoundsFetching,
        isUninitialized: isRoundsUninitialized
    } = useGetRoundsQuery(
        {
            compseasonId: compSeasonId,
            params: {
                pageSize: MAX_ROUNDS
            }
        },
        {
            skip: !compSeasonId
        }
    );

    /**
     * Fetching Brownlow Leaderboard data
     */
    const {
        data: leaderboardData,
        isFetching: isLeaderboardFetching,
        isUninitialized: isLeaderboardUninitialized
    } = useGetBFAwardsLeaderboardQuery(
        {
            seasonId: compSeasonPid
        },
        {
            skip: !(compSeasonPid && compSeasonPid !== '')
        }
    );

    // Enabling polling
    useLeaderboardRefetch(
        compSeasonPid,
        disablePolling ? 0 : PULSE.app.common.CONSTANTS.TIME.THIRTY_SECS_IN_MS
    );

    /**
     * Fetching matches data for the season
     */
    const { data: allSeasonMatches = [], isFetching: isMatchesFetching } =
        useGetMatchesQuery(
            {
                competitionId,
                compSeasonId,
                pageSize: 250
            },
            {
                skip: !competitionId || !compSeasonId
            }
        );

    /**
     * Fetching predictor data
     */
    const { data: predicted, isFetching: isPredictorFetching } =
        useGetBrownlowPredictorQuery(
            {
                compSeasonId: compSeasonId,
                players: currentPlayerIds?.join(',')
            },
            {
                skip: !(currentPlayerIds.length && compSeasonId)
            }
        );

    /**
     * Combined leaderboard data with matches
     */
    const leaderboardWithAllMatches = useMemo(() => {
        if (
            leaderboardData?.leaderboard.length &&
            allSeasonMatches?.length &&
            rounds
        ) {
            let leaderboardItems = leaderboardData.leaderboard;

            if (clubFilter !== 'all') {
                leaderboardItems = leaderboardItems.filter(
                    (item) =>
                        PULSE.app.common.team.getTeamAbbr(item.team?.teamId) ===
                        clubFilter
                );
            }

            return getLeaderboardWithAllMatches(
                leaderboardItems,
                allSeasonMatches,
                rounds
            );
        }

        return [];
    }, [rounds, leaderboardData, allSeasonMatches]);

    /**
     * Restructurin the data shape
     */
    useEffect(() => {
        if (leaderboardWithAllMatches.length) {
            let items = leaderboardWithAllMatches.map((item) => ({
                eligible: item.eligible ?? false,
                player: {
                    ...item.player,
                    leader: item.leader ?? false,
                    winner: item.winner ?? false
                },
                roundByRoundVotes: item.roundByRoundVotes ?? [],
                roundByRoundTotalVotes: item.roundByRoundTotalVotes ?? [],
                team: { ...item.team },
                totalVotes: item.totalVotes ?? ''
            }));

            setData(items);
        }
    }, [leaderboardWithAllMatches]);

    /**
     * Getting leadeboard rows with combined leadearboard, matches and predictor data
     */
    const rowData = useMemo(
        () =>
            data?.map((item) =>
                composeRowData(
                    item,
                    composeColumnData(rounds).filter(
                        (col) =>
                            !['player', 'totalVotes', 'bettingOdds'].includes(
                                col.accessor
                            )
                    ),
                    rounds,
                    false,
                    predicted?.results
                )
            ),
        [data, rounds, predicted?.results]
    );

    // Handles retrieving current player pulse IDs
    useCurrentPlayerPulseIds({
        rowData,
        currentPage,
        pageSize,
        currentPlayerIds,
        setCurrentPlayerIds
    });

    return {
        data: leaderboardData
            ? {
                  leaderboard: rowData,
                  status: leaderboardData?.status
              }
            : {},
        rounds,
        isLoading:
            (isRoundsFetching && isRoundsUninitialized) ||
            (isLeaderboardFetching && isLeaderboardUninitialized) ||
            isMatchesFetching ||
            isPredictorFetching
    };
};
